import React, {useCallback, useEffect, useState} from 'react';
import './report-subscription-tab.scss';
import ReportSubscriptionLogo from '@svg/report-subscription.svg?react';
import InfoIcon from '@svg/info-circle.svg?react';
import WhiteCheckmarkIcon from '@svg/white-checkmark.svg?react';
import {dateFormat, WORDS_MONTHS_FORMAT} from '@src/utils/datesUtils';
import Loader from '@src/components/shared/loader/loader';
import {useStores} from '@src/components/store';
import {Radio} from 'antd';
import {getNextMonthlyOccurrence, getNextWeeklyOccurrence,} from '@src/utils/occurrenceUtils';
import {SubscriptionFrequency, SubscriptionFrequencyLabel} from '@src/dtos/user-subscription.dto';
import {EventType} from '@src/dtos/analytics.dto';


const subscriptionFrequencyOptions = Object.entries(SubscriptionFrequency).map(([_, value]) => ({
    value: value,
    label: SubscriptionFrequencyLabel[value]
}));

const ReportSubscriptionTab: React.FC = () => {
    const { customerStore } = useStores();
    const [selectedFrequency, setSelectedFrequency] = useState<SubscriptionFrequency>(SubscriptionFrequency.OFF);
    const [loading, setLoading] = useState<boolean>(true);
    const [updating, setUpdating] = useState<boolean>(false);
    const [saved, setSaved] = useState<boolean>(false);

    useEffect(() => {
        const getSubscriptionData = async () => {
            try {
                setLoading(true);
                if(await customerStore.getIsEligibleForExecutiveReport()) {
                    setSelectedFrequency(await customerStore.getFrequencyOfExecutiveSummaryReport() ?? SubscriptionFrequency.OFF);
                }
            } catch (err) {
                console.error('Failed to fetch user Report Subscription data: ', err);
            } finally {
                setLoading(false);
            }
        };
        void getSubscriptionData();
    },[customerStore.customerId]);

    const updateReportSubscription = useCallback(() => {
        const updateSubscriptionData = async () => {
            try {
                setSaved(false);
                setUpdating(true);
                customerStore.sendGeneralEvent(EventType.LinkClicked,
                    {
                        button: 'Save', location: 'executiveSummaryReport', occurrence: selectedFrequency
                    });

                await customerStore.updateFrequencyOfExecutiveSummaryReport(selectedFrequency);
                setSaved(true);
            } catch (err) {
                console.error('Failed to update Report Subscription: ', err);
            } finally {
                setUpdating(false);
            }
        };
        void updateSubscriptionData();
    }, [customerStore.customerId, selectedFrequency]);

    if (loading) {
        return <Loader type={'full-screen'}/>;
    }

    const getNextOccurrence = ()=> {
        if(selectedFrequency === SubscriptionFrequency.MONTHLY) {
            return dateFormat(getNextMonthlyOccurrence(), WORDS_MONTHS_FORMAT);
        }
        return selectedFrequency === SubscriptionFrequency.WEEKLY ?
            dateFormat(getNextWeeklyOccurrence(), WORDS_MONTHS_FORMAT) : '';
    };

    const getSaveButtonProps = () => {
        if (saved && customerStore.frequencyOfExecutiveSummaryReport === selectedFrequency) {
            return {
                className: 'btn-saved',
                content: (
                    <>
                        <WhiteCheckmarkIcon className="checkmark-icon" />
                        Saved
                    </>
                )
            };
        }
        return { className: `${updating ? 'loading' : ''}`, content: 'Save' };
    };

    const { className: saveBtnClass, content: saveBtnContent } = getSaveButtonProps();

    return (
        <>
            {
                !customerStore.isEligibleForExecutiveReport && (
                    <div className="report-subscription-ineligible">
                        <InfoIcon/>
                        To subscribe to Executive summary report, you first need to apply a policy.
                    </div>
                )
            }
            <div className="report-subscription-tab">
                <div className="report-subscription-title-group">
                    <ReportSubscriptionLogo/>
                    <div className="tab-title">Executive summary report</div>
                </div>
                <div className="report-subscription-occurrence">
                    {`Next occurrence: ${getNextOccurrence()}`}
                </div>
                <div className="report-subscription-desc">Subscribe to executive summary report.</div>
                <div className="report-subscriptions-group">
                    <Radio.Group
                        name="radiogroup"
                        value={selectedFrequency}
                        options={subscriptionFrequencyOptions}
                        disabled={!customerStore.isEligibleForExecutiveReport || !customerStore.frequencyOfExecutiveSummaryReport}
                        onChange={(e) => setSelectedFrequency(e.target.value)}
                    />
                    <button type="button" className={`custom-btn primary-btn ${saveBtnClass}`}
                            onClick={updateReportSubscription}
                            disabled={!customerStore.isEligibleForExecutiveReport || !customerStore.frequencyOfExecutiveSummaryReport ||
                                updating || (customerStore.frequencyOfExecutiveSummaryReport === selectedFrequency)}>
                        {saveBtnContent}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ReportSubscriptionTab;